import {
  Box,
  Card,
  CardContent,
  Container,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';

import { useAddAioCallOutcomeMutation } from 'src/api/k8sQuery';
import { AIOOutcomeBackground } from 'src/app/components/aio/AIOOutcomeBackgroundImage';
import { AIOOutcomeCallOutcome } from 'src/app/components/aio/AIOOutcomeCallOutcome';
import { AIOOutcomeErrorCard } from 'src/app/components/aio/AIOOutcomeErrorCard';
import { AIOOutcomeFooter } from 'src/app/components/aio/AIOOutcomeFooter';
import { AIOOutcomeHeader } from 'src/app/components/aio/AIOOutcomeHeader';
import { AIOOutcomeKixieLogo } from 'src/app/components/aio/AIOOutcomeKixieLogo';
import { AIOOutcomeNotes } from 'src/app/components/aio/AIOOutcomeNotes';
import { AIOOutcomeSaveButton } from 'src/app/components/aio/AIOOutcomeSaveButton';
import { AIOOutcomeSuccessCard } from 'src/app/components/aio/AIOOutcomeSuccessCard';
import { Logger } from 'src/common/Logger';
import {
  makePHPRequest,
  methodNameEnum,
} from 'src/recoil/selectors/makePHPRequest';
import { endpointEnum, makeAPIRequest } from 'src/services/rest/makeAPIRequest';

import { IUserAtom, user } from '../../../recoil/atoms/user';

interface Disposition {
  businessDispositionId: number;
  disposition: string;
}

interface DispositionApiItem {
  disposition?: string;
  [key: string]: string | undefined;
}

export const AIOOutcome = () => {
  const [selectedDispositionId, setSelectedDispositionId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);

  const theme = useTheme();
  useMediaQuery(theme.breakpoints.down('md'));

  const [userAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtomContents = userAtom.contents as IUserAtom;

  const [addAioCallOutcome] = useAddAioCallOutcomeMutation();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const intentId = queryParams.get('intentid');
  const gtid = queryParams.get('gtid');
  const externalId = queryParams.get('externalid');

  const maxNotesLength = 1500;
  const isFormValid =
    selectedDispositionId !== '' && notes.length <= maxNotesLength;

  // Hide Intercom messenger and override dark mode on this page
  useEffect(() => {
    // Hide Intercom if it exists
    if (window.Intercom) {
      (window.Intercom as (
        action: string,
        options?: Record<string, unknown>
      ) => void)?.('update', { hide_default_launcher: true });
    }

    // Also hide it with CSS as a backup
    const intercomElements = document.querySelectorAll(
      '.intercom-lightweight-app, .intercom-messenger-frame, #intercom-container, .intercom-mobile-messenger'
    );
    intercomElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.style.display = 'none';
      }
    });

    // Store original body classes to restore later
    const originalBodyClasses = Array.from(document.body.classList);

    // Force light mode by removing dark mode classes and adding light mode class
    document.body.classList.remove('brand-dark');
    document.body.classList.add('brand-light');

    // Add global styles to override Material-UI dropdown dark mode
    const styleElement = document.createElement('style');
    styleElement.id = 'aio-outcome-light-mode-override';
    styleElement.textContent = `
      .MuiMenu-paper,
      .MuiPopover-paper,
      .MuiSelect-select {
        background-color: #fff !important;
        color: #000 !important;
      }
      .MuiMenuItem-root {
        background-color: #fff !important;
        color: #000 !important;
      }
      .MuiMenuItem-root:hover {
        background-color: #f5f5f5 !important;
      }
      .MuiMenuItem-root.Mui-selected {
        background-color: #f5f5f5 !important;
      }
      .MuiMenuItem-root.Mui-selected:hover {
        background-color: #eeeeee !important;
      }
      .MuiList-root {
        background-color: #fff !important;
      }
      /* Hide Intercom completely */
      .intercom-lightweight-app,
      .intercom-messenger-frame,
      #intercom-container,
      .intercom-mobile-messenger,
      div[id^="intercom"],
      iframe[src*="intercom"],
      .intercom-app,
      .intercom-widget-bubble {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        z-index: -9999 !important;
      }
    `;
    document.head.appendChild(styleElement);

    // Cleanup function to restore original state when leaving the page
    return () => {
      if (window.Intercom) {
        (window.Intercom as (
          action: string,
          options?: Record<string, unknown>
        ) => void)?.('update', { hide_default_launcher: false });
      }
      // Restore visibility
      intercomElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.style.display = '';
        }
      });

      // Restore original body classes
      document.body.className = originalBodyClasses.join(' ');

      // Remove the injected styles
      const injectedStyles = document.getElementById(
        'aio-outcome-light-mode-override'
      );
      if (injectedStyles) {
        injectedStyles.remove();
      }
    };
  }, []);

  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );

  const rawDispositions = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_DISPOSITION_REPORTING,
      payloadJsonString: JSON.stringify({
        timezone:
          businessData?.contents?.response?.data?.result?.[0]?.timezone_php,
      }),
      waitForPayload: !businessData?.contents?.response?.data?.result?.[0]
        ?.timezone_php,
    })
  );

  const dispositions: Disposition[] = useMemo(() => {
    if (
      rawDispositions.state === 'hasValue' &&
      rawDispositions.contents?.data?.dispositions?.result
    ) {
      return rawDispositions.contents.data.dispositions.result.map(
        (item: DispositionApiItem | string, index: number): Disposition => ({
          businessDispositionId: index,
          disposition: typeof item === 'string' ? item : item.disposition || '',
        })
      );
    }
    return [];
  }, [rawDispositions]);

  const handleCallOutcomeChange = (event: SelectChangeEvent<string>) => {
    setSelectedDispositionId(event.target.value);
  };

  const handleNotesChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setNotes(value);
  };

  const handleSave = async () => {
    if (isFormValid) {
      setIsSubmitting(true);
      setHasError(false);
      try {
        const selectedDisposition =
          dispositions[parseInt(selectedDispositionId)];

        const payload = {
          externalId,
          intentId: Number(intentId),
          globalTraceId: gtid,
          dispositionId: Number(selectedDispositionId),
          disposition: selectedDisposition?.disposition || '', // This is what logNoteCrm needs
          note: notes,
          apiKey: userAtomContents.apikey,
        };

        const response = await addAioCallOutcome({
          userSettings: {
            apikey: userAtomContents.apikey,
            businessId: userAtomContents.businessid,
            userid: userAtomContents.userid,
          },
          payload,
        });

        Logger.trace(response);

        if ('error' in response) {
          const errorStatus =
            'status' in response.error ? response.error.status : 'Unknown';
          Logger.error({
            message: `Error submitting call outcome. Status: ${errorStatus}`,
            error: response.error,
          });
          setHasError(true);
        } else if ('data' in response) {
          setIsSubmitted(true);
        } else {
          setHasError(true);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));

        Logger.error({
          message: `Error submitting call outcome ${error.message}`,
        });
        setHasError(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#F6F8FA !important',
        position: 'relative',
        p: { xs: 2, sm: 3 },
        color: '#000 !important',
        // Override any dark mode styles
        '& *': {
          color: 'inherit !important',
        },
        '& .MuiCard-root': {
          backgroundColor: '#fff !important',
          color: '#000 !important',
        },
        '& .MuiCardContent-root': {
          backgroundColor: '#fff !important',
        },
        '& .MuiTextField-root': {
          backgroundColor: '#fff !important',
          '& .MuiInputBase-root': {
            backgroundColor: '#fff !important',
            color: '#000 !important',
          },
          '& .MuiFormLabel-root': {
            color: '#666 !important',
          },
        },
        '& .MuiSelect-root': {
          backgroundColor: '#fff !important',
          color: '#000 !important',
        },
        '& .MuiButton-root:not([data-submitting="true"])': {
          color: '#fff !important',
        },
        // Override Material-UI dropdown menu dark mode styles
        '& .MuiMenu-paper': {
          backgroundColor: '#fff !important',
          color: '#000 !important',
        },
        '& .MuiMenuItem-root': {
          backgroundColor: '#fff !important',
          color: '#000 !important',
          '&:hover': {
            backgroundColor: '#f5f5f5 !important',
          },
          '&.Mui-selected': {
            backgroundColor: '#f5f5f5 !important',
            '&:hover': {
              backgroundColor: '#eeeeee !important',
            },
          },
        },
        '& .MuiList-root': {
          backgroundColor: '#fff !important',
        },
      }}
    >
      <AIOOutcomeBackground />
      <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
        <Container maxWidth="sm">
          <AIOOutcomeKixieLogo />
          {!externalId || !intentId || !gtid || hasError ? (
            <AIOOutcomeErrorCard />
          ) : isSubmitted ? (
            <AIOOutcomeSuccessCard />
          ) : (
            <Card
              elevation={1}
              sx={{
                borderRadius: 5,
                boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.02)',
                width: '100%',
                maxWidth: { xs: '100%', sm: 500, md: 666 },
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  '&:last-child': { pb: { xs: 3, sm: 4, md: 5 } },
                }}
              >
                <AIOOutcomeHeader />
                <AIOOutcomeCallOutcome
                  selectedDispositionId={selectedDispositionId}
                  handleCallOutcomeChange={handleCallOutcomeChange}
                  dispositions={dispositions}
                />
                <AIOOutcomeNotes
                  notes={notes}
                  handleNotesChange={handleNotesChange}
                  maxNotesLength={maxNotesLength}
                />
                <AIOOutcomeSaveButton
                  isSubmitting={isSubmitting}
                  isFormValid={isFormValid}
                  handleSave={handleSave}
                />
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <AIOOutcomeFooter />
      </Box>
    </Box>
  );
};
